import { useEffect } from 'react';
import { useCookieConsent } from '@/context/CookieConsentContext';

export default function PublicCookies() {
  const { openSettings } = useCookieConsent();
  useEffect(() => { document.title = 'Política de Cookies · Revix.es'; }, []);

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-[#1d1d1f]" data-testid="page-cookies">
      <header className="mb-10">
        <p className="text-xs uppercase tracking-wider text-[#6E6E73] mb-2">Legal</p>
        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">Política de Cookies</h1>
        <p className="text-sm text-[#6E6E73] mt-3">Última actualización: mayo 2026</p>
      </header>

      <section className="prose prose-slate max-w-none space-y-6 text-[15px] leading-relaxed">
        <h2 className="text-xl font-semibold mt-8">¿Qué son las cookies?</h2>
        <p>
          Las cookies son pequeños archivos de texto que se descargan en tu dispositivo cuando visitas
          una web. Permiten al sitio recordar información sobre tu visita (idioma, preferencias, sesión)
          o, según el tipo, recoger información estadística para mejorar la experiencia.
        </p>

        <h2 className="text-xl font-semibold mt-8">Cookies utilizadas en Revix.es</h2>
        <p>Clasificamos las cookies en tres categorías:</p>

        <div className="rounded-xl border border-[#E5E5EA] overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F8F9] text-[#6E6E73]">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Cookie</th>
                <th className="text-left px-4 py-2.5 font-medium">Tipo</th>
                <th className="text-left px-4 py-2.5 font-medium">Duración</th>
                <th className="text-left px-4 py-2.5 font-medium">Finalidad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5EA]">
              <tr>
                <td className="px-4 py-2.5 font-mono text-xs">revix_cookie_consent_v1</td>
                <td className="px-4 py-2.5">Propia · Necesaria</td>
                <td className="px-4 py-2.5">12 meses</td>
                <td className="px-4 py-2.5">Almacena tu elección sobre cookies.</td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 font-mono text-xs">session_id / token</td>
                <td className="px-4 py-2.5">Propia · Necesaria</td>
                <td className="px-4 py-2.5">Sesión / 24h</td>
                <td className="px-4 py-2.5">Mantener tu sesión iniciada en el área de cliente.</td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 font-mono text-xs">_ga</td>
                <td className="px-4 py-2.5">Tercero (Google) · Analítica</td>
                <td className="px-4 py-2.5">13 meses</td>
                <td className="px-4 py-2.5">Distinguir usuarios anónimamente para análisis estadístico.</td>
              </tr>
              <tr>
                <td className="px-4 py-2.5 font-mono text-xs">_ga_&lt;ID&gt;</td>
                <td className="px-4 py-2.5">Tercero (Google) · Analítica</td>
                <td className="px-4 py-2.5">13 meses</td>
                <td className="px-4 py-2.5">Persistir el estado de sesión analítica.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="text-xl font-semibold mt-8">Cookies propias y de terceros</h2>
        <p>
          Las <strong>cookies propias</strong> son gestionadas directamente por Revix.es. Las{' '}
          <strong>cookies de terceros</strong> son gestionadas por proveedores externos (actualmente
          Google Analytics 4). En todo caso, antes de instalarlas pedimos tu consentimiento expreso.
        </p>

        <h2 className="text-xl font-semibold mt-8">Consent Mode v2</h2>
        <p>
          Implementamos Google Consent Mode v2: por defecto los almacenamientos de analítica y
          publicidad están <code>denied</code> y solo se activan tras tu aceptación. La IP se
          anonimiza y los datos se tratan de forma agregada.
        </p>

        <h2 className="text-xl font-semibold mt-8">Cómo cambiar tus preferencias</h2>
        <p>
          Puedes cambiar de opinión en cualquier momento haciendo clic en{' '}
          <button
            type="button"
            onClick={openSettings}
            className="underline underline-offset-2 font-medium hover:text-[#0066CC]"
            data-testid="cookies-page-open-settings"
          >Configurar cookies</button>{' '}
          o desde el enlace "Cookies" disponible en el pie de página.
        </p>
        <p>
          También puedes eliminar las cookies directamente desde la configuración de tu navegador
          (Chrome, Safari, Firefox, Edge…). Ten en cuenta que rechazar las cookies necesarias puede
          impedir el funcionamiento normal del sitio.
        </p>

        <h2 className="text-xl font-semibold mt-8">Contacto</h2>
        <p>
          Para cualquier duda sobre cookies o privacidad, escríbenos a{' '}
          <a className="underline" href="mailto:privacidad@revix.es">privacidad@revix.es</a>.
        </p>
      </section>
    </article>
  );
}
